import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  Statistic,
  message,
  Empty,
  Tag,
  TablePaginationConfig,
} from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main, TableWrapper, TopToolBox } from 'container/styled';
import { Cards } from 'components/cards/frame/cards-frame';
import { CustomTableStyleWrapper } from 'pages/style';
import FeatherIcon from 'feather-icons-react';
import moment, { Moment } from 'moment';
import { formatCurrency, formatDate } from 'utility/utility';
import { ColumnsType } from 'antd/lib/table';
import { PaymentsApi } from 'api/clients/payments-api';
import { UpcomingPaymentsResultDto, UpcomingPaymentRowDto } from 'api/models';
import { ProjectsApi } from 'api/api';
import { ProjectDto } from 'api/models';
import { AutoComplete } from 'components/autoComplete/autoComplete';

const { RangePicker } = DatePicker;

const paymentsApi = new PaymentsApi();
const projectsApi = new ProjectsApi();

const UpcomingPayments: React.FC = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [data, setData] = useState<UpcomingPaymentsResultDto | null>(null);
  const [projects, setProjects] = useState<ProjectDto[]>([]);

  const [query, setQuery] = useState({
    dateRange: [moment(), moment().add(30, 'days')] as [Moment, Moment] | null,
    projectId: undefined as number | undefined,
    search: '',
    sort: 'ExpectedDate',
    pageNumber: 1,
    pageSize: 25,
  });

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 25,
    total: 0,
  });

  // Load projects for filter dropdown
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setProjectsLoading(true);
        const response = await projectsApi.apiProjectsGet({
          pageSize: -1,
          isActive: true,
        });
        setProjects(response.data.items || []);
      } catch (error) {
        console.error('Failed to load projects:', error);
      } finally {
        setProjectsLoading(false);
      }
    };
    loadProjects();
  }, []);

  const fetchData = useCallback(async () => {
    if (!query.dateRange || query.dateRange.length !== 2) {
      message.error('Please select a date range');
      return;
    }

    setLoading(true);
    try {
      const [from, to] = query.dateRange;
      const result = await paymentsApi.apiPaymentsUpcomingGet({
        from: from.format('YYYY-MM-DD'),
        to: to.format('YYYY-MM-DD'),
        projectId: query.projectId,
        search: query.search || undefined,
        sort: query.sort,
        pageNumber: query.pageNumber,
        pageSize: query.pageSize,
      });

      setData(result.data);
      setPagination({
        current: result.data.pageNumber || 1,
        pageSize: query.pageSize,
        total: result.data.totalCount || 0,
      });
    } catch (error) {
      console.error('Failed to fetch upcoming payments:', error);
      message.error('Failed to fetch upcoming payments');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (value: string) => {
    setQuery(prev => ({ ...prev, search: value, pageNumber: 1 }));
  };

  const handleProjectFilterChange = (value: number) => {
    setQuery(prev => ({
      ...prev,
      projectId: value === -1 ? undefined : value,
      pageNumber: 1,
    }));
  };

  const handleDateRangeChange = (dates: any) => {
    setQuery(prev => ({ ...prev, dateRange: dates as [Moment, Moment] | null, pageNumber: 1 }));
  };

  const handleReset = () => {
    setQuery({
      dateRange: [moment(), moment().add(30, 'days')],
      projectId: undefined,
      search: '',
      sort: 'ExpectedDate',
      pageNumber: 1,
      pageSize: 25,
    });
  };

  const getDaysUntilDueColor = (days: number) => {
    if (days < 0) return 'red';
    if (days <= 7) return 'orange';
    if (days <= 30) return 'blue';
    return 'green';
  };

  const columns: ColumnsType<UpcomingPaymentRowDto> = [
    {
      title: t('payments:client', 'Client'),
      dataIndex: 'clientName',
      key: 'clientName',
      render: (text: string, record) => (
        <Link to={`/clients/${record.clientId || 0}`}>{text || '-'}</Link>
      ),
      sorter: true,
    },
    {
      title: t('payments:email', 'Email'),
      dataIndex: 'clientEmail',
      key: 'clientEmail',
      render: (email?: string) => email || '-',
    },
    {
      title: t('payments:deal', 'Deal'),
      dataIndex: 'dealName',
      key: 'dealName',
      render: (text: string, record) => (
        <Link to={`/deals/${record.dealId || 0}`}>{text || '-'}</Link>
      ),
    },
    {
      title: t('payments:project', 'Project'),
      dataIndex: 'projectName',
      key: 'projectName',
      render: (text: string, record) => (
        <Link to={`/projects/${record.projectId || 0}`}>{text || '-'}</Link>
      ),
      sorter: true,
    },
    {
      title: t('payments:expected-amount', 'Expected Amount'),
      dataIndex: 'expectedAmount',
      key: 'expectedAmount',
      render: (amount: number) => amount ? formatCurrency(amount) : '-',
      align: 'right' as const,
      sorter: true,
    },
    {
      title: t('payments:expected-date', 'Expected Date'),
      dataIndex: 'expectedDate',
      key: 'expectedDate',
      render: (date: string) => date ? formatDate(date) : '-',
      sorter: true,
    },
    {
      title: t('payments:days-until-due', 'Days Until Due'),
      dataIndex: 'daysUntilDue',
      key: 'daysUntilDue',
      render: (days: number) => days ? (
        <Tag color={getDaysUntilDueColor(days)}>
          {days < 0 ? `${Math.abs(days)} days overdue` : `${days} days`}
        </Tag>
      ) : '-',
      align: 'center' as const,
    },
  ];

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={t('payments:upcoming-payments', 'Upcoming Payments')}
        />
      </CardToolbox>

      <Main>

        {/* Overview Cards */}
        {data?.overview && (
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Cards>
                <Statistic
                  title={t('payments:total-expected-amount', 'Total Expected Amount')}
                  value={data.overview.totalExpectedAmount}
                  formatter={(value) => formatCurrency(Number(value))}
                  prefix={<FeatherIcon icon="dollar-sign" size={16} />}
                />
              </Cards>
            </Col>
            {query.projectId && data.overview.totalExpectedAmountByProject && data.overview.totalExpectedAmountByProject > 0 && (
              <Col xs={24} sm={12} lg={6}>
                <Cards>
                  <Statistic
                    title={t('payments:project-expected-amount', 'Selected Project Amount')}
                    value={data.overview.totalExpectedAmountByProject}
                    formatter={(value) => formatCurrency(Number(value))}
                    prefix={<FeatherIcon icon="briefcase" size={16} />}
                  />
                </Cards>
              </Col>
            )}
            <Col xs={24} sm={12} lg={6}>
              <Cards>
                <Statistic
                  title={t('payments:count-payments', 'Upcoming Payments')}
                  value={data.overview.countPayments}
                  prefix={<FeatherIcon icon="calendar" size={16} />}
                />
              </Cards>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Cards>
                <Statistic
                  title={t('payments:date-range', 'Date Range')}
                  value={`${formatDate(data.overview.rangeTo)}`}
                  prefix={<FeatherIcon icon="clock" size={16} />}
                />
              </Cards>
            </Col>
          </Row>
        )}

        {/* Filters */}
        <Cards headless>
          <Row gutter={15}>
            <Col xs={24}>
              <TopToolBox>
                <Row gutter={15}>
                  <Col lg={6} md={6} xs={24}>
                    <div className="table-search-box">
                      <AutoComplete
                        onSearch={handleSearch}
                        width="100%"
                        patterns
                        placeholder={t('payments:search', 'Search by client, deal, email...')}
                      />
                    </div>
                  </Col>

                  <Col lg={12} md={14} xs={24}>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{t('payments:date-range', 'Date Range:')}</span>
                        <RangePicker
                          value={query.dateRange}
                          onChange={handleDateRangeChange}
                          placeholder={[t('payments:from-date', 'From'), t('payments:to-date', 'To')]}
                          style={{ width: 250 }}
                        />
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{t('projects:project-filter', 'Project:')}</span>
                        <Select
                          style={{ width: 200 }}
                          onChange={handleProjectFilterChange}
                          value={query.projectId ?? -1}
                          loading={projectsLoading}
                        >
                          <Select.Option value={-1}>{t('global:all', 'All')}</Select.Option>
                          {projects.map(project => (
                            <Select.Option key={project.id} value={project.id}>
                              {project.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    </div>
                  </Col>

                  {/* <Col lg={6} md={4} xs={24}>
                    <Button onClick={handleReset} icon={<FeatherIcon icon="refresh-ccw" size={16} />}>
                      {t('common:reset', 'Reset')}
                    </Button>
                  </Col> */}
                </Row>

                {/* <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                  <Col>
                    <Button
                      size="small"
                      onClick={() => handleDateRangeChange([moment(), moment().add(7, 'days')])}
                    >
                      {t('payments:next-7-days', 'Next 7 days')}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      size="small"
                      onClick={() => handleDateRangeChange([moment(), moment().add(30, 'days')])}
                    >
                      {t('payments:next-30-days', 'Next 30 days')}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      size="small"
                      onClick={() => handleDateRangeChange([moment(), moment().endOf('month')])}
                    >
                      {t('payments:end-of-month', 'End of month')}
                    </Button>
                  </Col>
                </Row> */}
              </TopToolBox>
            </Col>
          </Row>

          <CustomTableStyleWrapper>
            <TableWrapper>
              <Table
                loading={loading}
                dataSource={data?.items || []}
                columns={columns}
                pagination={{
                  current: query.pageNumber,
                  pageSize: query.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 25, 50, 100],
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} ${t('global.of', 'of')} ${total} ${t('global.items', 'items')}`,
                  onChange: (pageNumber, pageSize) => setQuery(prev => ({ ...prev, pageNumber, pageSize })),
                }}
                rowKey="paymentId"
                locale={{
                  emptyText: (
                    <Empty
                      description={t('payments:no-upcoming-payments', 'No upcoming payments found')}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
                scroll={{ x: 800 }}
              />
            </TableWrapper>
          </CustomTableStyleWrapper>
        </Cards>
      </Main>
    </>
  );
};

export default UpcomingPayments;